import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, X, Building2, Users, CheckSquare, Phone,
  FileText, Hotel, Loader2, ArrowRight, Activity
} from 'lucide-react';
import { debounce } from 'lodash';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const abortRef = useRef(null);
  const performSearch = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults(null);
        setLoading(false);
        return;
      }
      // Cancel any stale in-flight search before starting a new one.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/api/search/global?q=${encodeURIComponent(searchQuery)}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        if (response.ok) setResults(await response.json());
        else setResults(null);
      } catch (err) {
        if (err.name !== 'AbortError') setResults(null);
      }
      finally { if (abortRef.current === controller) setLoading(false); }
    }, 300),
    []
  );

  useEffect(() => { performSearch(query); return () => performSearch.cancel(); }, [query, performSearch]);

  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && inputRef.current && !inputRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const getAllItems = () => {
    if (!results) return [];
    return [
      ...(results.leads || []).map(l => ({ type: 'lead', item: l })),
      ...(results.properties || []).map(p => ({ type: 'property', item: p })),
      ...(results.lifecycle || []).map(lc => ({ type: 'lifecycle', item: lc })),
      ...(results.tasks || []).map(t => ({ type: 'task', item: t })),
      ...(results.calls || []).map(c => ({ type: 'call', item: c })),
      ...(results.users || []).map(u => ({ type: 'user', item: u })),
    ];
  };

  const handleKeyDown = (e) => {
    if (!isOpen || !results) return;
    const allItems = getAllItems();
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, -1)); }
    else if (e.key === 'Enter' && selectedIndex >= 0) { e.preventDefault(); const s = allItems[selectedIndex]; handleResultClick(s.type, s.item); }
    else if (e.key === 'Escape') { setIsOpen(false); setQuery(''); }
  };

  const handleResultClick = (type, item) => {
    setIsOpen(false); setQuery('');
    switch (type) {
      case 'lead': navigate(`/leads/${item.id}`); break;
      case 'property': navigate(`/properties/${item.id}`); break;
      case 'lifecycle': navigate(`/lifecycle/${item.lead_id}`); break;
      case 'task': navigate(`/leads/${item.lead_id}`); break;
      case 'call': navigate('/call-history'); break;
      case 'user': navigate('/settings/users'); break;
      default: break;
    }
  };

  const sections = [
    { key: 'leads', label: 'Leads', icon: <Users size={14} className="text-blue-500" />, color: 'blue' },
    { key: 'properties', label: 'Properties', icon: <Hotel size={14} className="text-purple-500" />, color: 'purple' },
    { key: 'lifecycle', label: 'Lifecycle', icon: <Activity size={14} className="text-emerald-500" />, color: 'emerald' },
    { key: 'tasks', label: 'Tasks', icon: <CheckSquare size={14} className="text-orange-500" />, color: 'orange' },
    { key: 'calls', label: 'Calls', icon: <Phone size={14} className="text-cyan-500" />, color: 'cyan' },
    { key: 'users', label: 'Users', icon: <Building2 size={14} className="text-teal-500" />, color: 'teal' },
  ];

  const hasResults = results && sections.some(s => results[s.key]?.length > 0);
  const totalResults = results ? sections.reduce((sum, s) => sum + (results[s.key]?.length || 0), 0) : 0;

  const renderItem = (type, item, isSelected) => {
    const base = `w-full px-4 py-2.5 text-left hover:bg-teal-50 transition-colors flex items-center gap-3 ${isSelected ? 'bg-teal-50' : ''}`;
    switch (type) {
      case 'lead': return (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-800 truncate">{item.hotel_name}</p>
            {item.grb_property_id && <span className="text-[10px] font-bold font-mono px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">{item.grb_property_id}</span>}
          </div>
          <p className="text-xs text-gray-500 truncate">{item.owner_name} {item.phone ? `• ${item.phone}` : ''} {item.city ? `• ${item.city}` : ''}</p>
        </div>
      );
      case 'property': return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{item.propertyName || item.name}</p>
          <p className="text-xs text-gray-500 truncate">{item.ownerName} {item.city ? `• ${item.city}` : ''}</p>
        </div>
      );
      case 'lifecycle': return (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-800 truncate">{item.hotel_name}</p>
            {item.grb_property_id && <span className="text-[10px] font-bold font-mono px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">{item.grb_property_id}</span>}
          </div>
          <p className="text-xs text-gray-500 truncate">
            Status: {item.status || '-'} • Onboarding: {item.onboarding_status || '-'}
            {item.offboarding_status ? ` • Offboarding: ${item.offboarding_status}` : ''}
          </p>
        </div>
      );
      case 'task': return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{item.title}</p>
          <p className="text-xs text-gray-500 truncate">{item.hotel_name} • {item.status}</p>
        </div>
      );
      case 'call': return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{item.contact_name || item.phone}</p>
          <p className="text-xs text-gray-500 truncate">{item.agent_name} • {item.status} {item.date ? `• ${new Date(item.date).toLocaleDateString()}` : ''}</p>
        </div>
      );
      case 'user': return (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
          <p className="text-xs text-gray-500 truncate">{item.email} • {item.role}</p>
        </div>
      );
      default: return null;
    }
  };

  const colorMap = { blue: 'bg-blue-100 text-blue-600', purple: 'bg-purple-100 text-purple-600', emerald: 'bg-emerald-100 text-emerald-600', orange: 'bg-orange-100 text-orange-600', cyan: 'bg-cyan-100 text-cyan-600', teal: 'bg-teal-100 text-teal-600' };
  const initialMap = { lead: (i) => i.hotel_name?.charAt(0) || 'L', property: (i) => (i.propertyName || i.name || 'P').charAt(0), lifecycle: (i) => i.hotel_name?.charAt(0) || 'H', task: () => null, call: (i) => (i.contact_name || 'C').charAt(0), user: (i) => (i.name || 'U').charAt(0) };

  let runningIdx = 0;

  return (
    <div className="relative" data-testid="global-search">
      <div className="relative">
        <input ref={inputRef} type="text" value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setSelectedIndex(-1); }}
          onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown}
          placeholder="Search leads, properties, task..."
          className="pl-10 pr-10 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent w-72 transition-all bg-gray-50 focus:bg-white"
          data-testid="global-search-input" />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        {query && <button onClick={() => { setQuery(''); setResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={16} /></button>}
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-500 animate-spin" size={16} />}
      </div>

      {isOpen && query.length >= 2 && (
        <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading ? (
            <div className="p-4 text-center text-gray-500"><Loader2 className="animate-spin mx-auto mb-2" size={24} /><p className="text-sm">Searching...</p></div>
          ) : hasResults ? (
            <div className="divide-y divide-gray-100">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs text-gray-500 font-medium">{totalResults} result{totalResults !== 1 ? 's' : ''} found</p>
              </div>
              {sections.map(sec => {
                const items = results[sec.key] || [];
                if (items.length === 0) return null;
                const sectionStart = runningIdx;
                const rendered = (
                  <div key={sec.key} className="py-2">
                    <div className="px-4 py-1 flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase">
                      {sec.icon} {sec.label} ({items.length})
                    </div>
                    {items.map((item, idx) => {
                      const itemIdx = sectionStart + idx;
                      const typeKey = sec.key === 'leads' ? 'lead' : sec.key === 'properties' ? 'property' : sec.key === 'lifecycle' ? 'lifecycle' : sec.key === 'tasks' ? 'task' : sec.key === 'calls' ? 'call' : 'user';
                      const initial = initialMap[typeKey]?.(item);
                      return (
                        <button key={item.id || idx} onClick={() => handleResultClick(typeKey, item)}
                          className={`w-full px-4 py-2.5 text-left hover:bg-teal-50 transition-colors flex items-center gap-3 ${selectedIndex === itemIdx ? 'bg-teal-50' : ''}`}>
                          {typeKey === 'task' ? (
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}><CheckSquare size={16} /></div>
                          ) : (
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm ${colorMap[sec.color]}`}>{initial}</div>
                          )}
                          {renderItem(typeKey, item, selectedIndex === itemIdx)}
                          <ArrowRight size={14} className="text-gray-400 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                );
                runningIdx += items.length;
                return rendered;
              })}
            </div>
          ) : (
            <div className="p-6 text-center">
              <Search className="mx-auto mb-2 text-gray-300" size={32} />
              <p className="text-sm text-gray-500">No results found for "{query}"</p>
              <p className="text-xs text-gray-400 mt-1">Try different keywords</p>
            </div>
          )}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
            <span><kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">↑↓</kbd> Navigate</span>
            <span><kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">Enter</kbd> Select</span>
            <span><kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">Esc</kbd> Close</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
